import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import { motion, useInView } from 'framer-motion';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled(motion.section)`
  max-width: 1600px;
  margin: 0 auto;
  padding: var(--space-5xl) var(--container-padding);
  position: relative;

  /* Background text */
  &::before {
    content: 'ABOUT';
    position: absolute;
    top: 5%;
    right: 5%;
    font-size: clamp(150px, 25vw, 350px);
    font-weight: 700;
    color: rgba(0, 0, 0, 0.02);
    letter-spacing: 0.05em;
    z-index: 0;
    pointer-events: none;
    font-family: var(--font-display);
    text-transform: uppercase;
  }

  .section-header {
    margin-bottom: var(--space-4xl);
    position: relative;
    z-index: 1;
  }

  .content-grid {
    display: grid;
    grid-template-columns: 1.2fr 1fr;
    gap: var(--space-5xl);
    align-items: start;
    position: relative;
    z-index: 1;

    @media (max-width: 968px) {
      grid-template-columns: 1fr;
      gap: var(--space-4xl);
    }
  }
`;

const StyledText = styled(motion.div)`
  p {
    font-size: clamp(18px, 2vw, 22px);
    line-height: 1.9;
    color: var(--text-tertiary);
    margin-bottom: var(--space-xl);
    opacity: 0.85;
    font-weight: 300;
  }

  .skills-container {
    margin-top: var(--space-3xl);
    padding-top: var(--space-3xl);
    border-top: 1px solid var(--border-color);
  }

  .skills-label {
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
    color: var(--text-tertiary);
    margin-bottom: var(--space-lg);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    opacity: 0.7;
  }

  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-md) var(--space-lg);
    padding: 0;
    margin: 0;
    list-style: none;

    li {
      position: relative;
      padding-left: var(--space-xl);
      font-family: var(--font-mono);
      font-size: var(--fz-sm);
      color: var(--text-secondary);
      opacity: 0.9;

      &:before {
        content: '—';
        position: absolute;
        left: 0;
        color: var(--text-primary);
        font-size: 1em;
        font-weight: 300;
        opacity: 0.5;
      }
    }
  }
`;

const StyledPic = styled(motion.div)`
  position: sticky;
  top: calc(var(--nav-height) + var(--space-xl));
  max-width: 100%;

  @media (max-width: 968px) {
    position: relative;
    top: 0;
    max-width: 400px;
    margin: 0 auto;
  }

  .wrapper {
    position: relative;
    width: 100%;
    aspect-ratio: 3/4;
    border-radius: var(--border-radius);
    overflow: hidden;
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    transform: translateX(0);

    &::after {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      border: 1px solid var(--text-primary);
      top: 12px;
      left: 12px;
      z-index: -1;
      opacity: 0.2;
      border-radius: var(--border-radius);
      transition: var(--transition-base);
    }

    &:hover {
      &::after {
        transform: translate(6px, 6px);
        opacity: 0.4;
      }

      .img {
        filter: grayscale(0%) contrast(1.05);
        transform: scale(1.03);
      }
    }

    .img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      filter: grayscale(10%) contrast(1.05);
      transition: all var(--transition-slow);
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const isInView = useInView(revealContainer, { once: true, margin: '-100px' });

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skills = [
    'JavaScript (ES6+)', 
    'TypeScript', 
    'Python', 
    'Go', 
    'Java', 
    'React.js', 
    'Next.js', 
    'PyTorch', 
    'LangChain', 
    'FastAPI', 
    'MongoDB', 
    'PostgreSQL', 
    'Docker', 
    'AWS', 
    'Azure', 
    'CI/CD'
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.6, -0.05, 0.01, 0.99],
      },
    },
  };

  return (
    <StyledAboutSection
      id="about"
      ref={revealContainer}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={containerVariants}>
      <motion.div className="section-header" variants={itemVariants}>
        <h2 className="numbered-heading">About Me</h2>
      </motion.div>

      <div className="content-grid">
        <StyledText variants={itemVariants}>
          <div>
            <motion.p variants={itemVariants}>
              Full-Stack and Applied AI/ML Engineer experienced in building scalable, production-ready systems that integrate modern web technologies with multimodal machine learning. Strong background in real-time inference, cloud-native APIs, and AI-driven automation across enterprise and research environments.
            </motion.p>
            <motion.p variants={itemVariants}>
              Currently pursuing a Master of Science in Computer Science at University of North Carolina at Charlotte (Expected May 2026), I'm working as an AI Engineer Intern at TheoremLabs and as a Research Assistant at AI4Health and UQ Labs, focusing on cutting-edge AI/ML research and enterprise automation solutions.
            </motion.p>
          </div>

          <motion.div className="skills-container" variants={itemVariants}>
            <div className="skills-label">Technologies I work with:</div>
            <ul className="skills-list">
              {skills && skills.map((skill, i) => (
                <motion.li
                  key={i}
                  variants={itemVariants}
                  initial="hidden"
                  animate={isInView ? 'visible' : 'hidden'}
                  transition={{ delay: i * 0.05 }}>
                  {skill}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </StyledText>

        <StyledPic
          variants={itemVariants}
          whileHover={{ scale: 1.02 }}
          transition={{ type: 'spring', stiffness: 300 }}>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
